/**
 * TEMPORARY — RC-APP-FREEZE-1
 * Remove after diagnosis. Observation only; no behavior change.
 */

const PREFIX = "[fivi-diag]";

let started = false;
let viewerCreates = 0;
let viewerDestroys = 0;
let viewerSeq = 0;
let longestTaskMs = 0;

export function isFreezeDiagEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  if (typeof process !== "undefined" && process.env.NODE_ENV === "test") {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("diag") === "freeze") {
      return true;
    }
    if (window.localStorage?.getItem("fivi-freeze-diag") === "1") {
      return true;
    }
  } catch {
    // ignore storage/search errors
  }

  return typeof process !== "undefined" && process.env.NODE_ENV === "development";
}

export function diagLog(event, details) {
  if (!isFreezeDiagEnabled()) {
    return;
  }

  if (details !== undefined) {
    console.info(PREFIX, event, details);
    return;
  }

  console.info(PREFIX, event);
}

export function diagWarn(event, details) {
  if (!isFreezeDiagEnabled()) {
    return;
  }

  if (details !== undefined) {
    console.warn(PREFIX, event, details);
    return;
  }

  console.warn(PREFIX, event);
}

export function nextViewerDiagId() {
  viewerSeq += 1;
  return `pv-${viewerSeq}`;
}

export function trackViewerCreate(id, extra) {
  viewerCreates += 1;
  diagLog("viewer create", {
    id,
    live: viewerCreates - viewerDestroys,
    totalCreates: viewerCreates,
    ...extra,
  });
}

export function trackViewerDestroy(id, extra) {
  viewerDestroys += 1;
  diagLog("viewer destroy", {
    id,
    live: viewerCreates - viewerDestroys,
    totalDestroys: viewerDestroys,
    ...extra,
  });
}

function megabytes(bytes) {
  return Math.round((bytes / 1048576) * 10) / 10;
}

export function memorySnapshot() {
  const memory = performance.memory;
  if (!memory) {
    return null;
  }

  return {
    usedMB: megabytes(memory.usedJSHeapSize),
    totalMB: megabytes(memory.totalJSHeapSize),
    limitMB: megabytes(memory.jsHeapSizeLimit),
  };
}

function countNodes() {
  return {
    canvases: document.querySelectorAll("canvas").length,
    images: document.querySelectorAll("img").length,
    openOverlays: document.querySelectorAll('[data-state="open"]').length,
    pannellumContainers: document.querySelectorAll(".pnlm-container").length,
  };
}

export function startFreezeDiagnostics() {
  if (started || !isFreezeDiagEnabled()) {
    return;
  }

  started = true;

  diagLog("start", {
    href: window.location.href,
    memory: memorySnapshot(),
    ua: navigator.userAgent,
    listenerInventory: {
      auth: "onAuthStateChanged — AuthProvider mount/unmount",
      firestore: "getDoc/getDocs only on project/images/hotspots — no onSnapshot",
      viewer: "PanoramaViewer recreates Pannellum on panoramaUrl change",
      upload: "processImageForUpload caps longest edge at 8192, WebP 0.90, then thumbnail ≤1200 WebP 0.75, releases canvas/bitmap",
      previews: "ImageCard uses real previewUrl thumbnail only; legacy docs show placeholder; viewer uses originalUrl",
    },
  });

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const duration = Math.round(entry.duration);
        if (duration > longestTaskMs) {
          longestTaskMs = duration;
        }
        if (duration >= 100) {
          diagWarn("longtask", {
            durationMs: duration,
            startTime: Math.round(entry.startTime),
            name: entry.name,
          });
        }
      }
    });
    observer.observe({ type: "longtask", buffered: true });
  } catch (error) {
    diagLog("longtask observer unavailable", String(error?.message || error));
  }

  document.addEventListener(
    "webglcontextlost",
    (event) => {
      diagWarn("webglcontextlost", {
        defaultPrevented: event.defaultPrevented,
        tag: event.target?.tagName,
      });
    },
    true,
  );

  document.addEventListener(
    "load",
    (event) => {
      const element = event.target;
      if (!(element instanceof HTMLImageElement)) {
        return;
      }

      const width = element.naturalWidth;
      const height = element.naturalHeight;
      if (width * height < 4_000_000) {
        return;
      }

      diagWarn("img-decode-large", {
        width,
        height,
        mp: Math.round((width * height) / 100000) / 10,
        rgbaMB: Math.round((width * height * 4) / 1048576),
        src: (element.currentSrc || element.src || "").slice(0, 160),
      });
    },
    true,
  );

  window.setInterval(() => {
    diagLog("heartbeat", {
      memory: memorySnapshot(),
      nodes: countNodes(),
      longestTaskMs,
      viewerCreates,
      viewerDestroys,
      liveViewers: viewerCreates - viewerDestroys,
    });
  }, 10000);

  window.__FIVI_FREEZE_DIAG_SNAPSHOT__ = () => ({
    memory: memorySnapshot(),
    nodes: countNodes(),
    longestTaskMs,
    viewerCreates,
    viewerDestroys,
    liveViewers: viewerCreates - viewerDestroys,
  });
}
